import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MenuItem } from "src/molecules";
import { CustomerMessages } from "@languages/interfaces";
import * as appIcons from "@assets/applications";
import { useAuth } from "@hooks/useAuth";
import { usePermissions } from "@pages/Office";

const MenuItems: MenuItem[] = [
  {
    id: "1",
    title: CustomerMessages.applicationDashboard,
    icon: appIcons.dashboard,
    link: "/",
    hidden: false
  },
  {
    id: "2",
    title: CustomerMessages.applicationConcretingRequest,
    icon: appIcons.menuConcretingRequest,
    link: "/concreting-requests",
    hidden: true
  },
  {
    id: "3",
    title: CustomerMessages.applicationContract,
    icon: appIcons.menuContract,
    link: "/contracts",
    hidden: true
  },
  {
    id: "4",
    title: CustomerMessages.applicationConcreting,
    icon: appIcons.menuConcreting,
    link: "/concretings",
    hidden: true
  },
  {
    id: "5",
    title: CustomerMessages.applicationInvoice,
    icon: appIcons.menuInvoice,
    link: "/invoices",
    hidden: true
  },
  {
    id: "6",
    title: CustomerMessages.applicationConcretingApproval,
    icon: appIcons.menuConcretingApproval,
    link: "/concreting-approvals",
    onlyAdmin: true
  },
  {
    id: "7",
    title: CustomerMessages.applicationCertificate,
    icon: appIcons.menuCertificate,
    link: "/certificates",
    hidden: true
  },
  {
    id: "8",
    title: CustomerMessages.applicationClient,
    icon: appIcons.menuClient,
    link: "/clients",
    hidden: true
  },
  {
    id: "9",
    title: CustomerMessages.applicationPermission,
    icon: appIcons.menuPermission,
    link: "/permissions",
    onlyAdmin: true
  }
];

interface SidebarMenuContextType {
  menuItems: MenuItem[];
  setMenuItems: React.Dispatch<React.SetStateAction<MenuItem[]>>;
}

const SidebarMenuContext = createContext<SidebarMenuContextType | undefined>(undefined);

export const SidebarMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { formattedPermissions } = usePermissions();
  const [menuItems, setMenuItems] = useState<MenuItem[]>(MenuItems);

  const updateMenuMobile = useCallback(
    (permissions: Record<string, { modules: (string | null)[] }>) => {
      const allowedModules = new Set(
        Object.values(permissions)
          .flatMap((perm) => perm.modules)
          .filter((mod): mod is string => mod !== null)
      );

      setMenuItems((prevMenuItems) => prevMenuItems.map((item) => ({
        ...item,
        hidden: item.id !== "1" && !allowedModules.has(`${item.link}`)
      })));

    },
    []
  );

  useEffect(() => {
    if (!user.admin && formattedPermissions) {
      updateMenuMobile(formattedPermissions);
    }
  }, [user, formattedPermissions, updateMenuMobile]);

  const sidebarMenuState = useMemo(() => ({ menuItems, setMenuItems }), [menuItems, setMenuItems]);

  return (
    <SidebarMenuContext.Provider value={sidebarMenuState}>
      {children}
    </SidebarMenuContext.Provider>
  );
};

export const useSidebarMenu = () => {
  const context = useContext(SidebarMenuContext);

  if (!context) {
    throw new Error("useSidebarMenu must be used inside a SidebarMenuProvider");
  }

  return context;
};